<template>
  <v-container>
    <h1>{{ $t('release-notes.title') }}</h1>

    <div class="w-full d-flex">
      <ActionButton
        type="action center"
        class="ml-auto w-auto"
        :block="false"
        :isDisabled="isImitatingUser()"
        @click="markAllAsRead()"
      >
        {{ $t("labels.mark-all-as-read") }}
      </ActionButton>
    </div>

    <v-data-table
      :headers="headers"
      :items="releaseNotes"
      :loading="loading"
      :items-per-page="-1"
      :hide-default-footer="true"
      show-expand
      single-expand
      :expanded.sync="expanded"
      item-key="id"
      :no-data-text="$t('release-notes.no-data')"
      class="elevation-1 mt-4"
    >
      <template v-slot:[`item.version`]="{item}">
        <strong>{{ item.version }}</strong>
      </template>

      <template v-slot:[`item.summary`]="{item}">
        <div class="d-flex align-center" style="column-gap: 8px">
          <div style="flex-shrink: 0">
            <v-chip v-if="item.isCurrent" small color="secondary" class="font-weight-bold">
              {{ $t('release-notes.current') }}
            </v-chip>
          </div>
          <div>{{ item.summary }}</div>
        </div>
      </template>

      <template v-slot:[`item.releaseDate`]="{item}">
        {{ formatDate(item.releaseDate) }}
      </template>

      <template v-slot:[`item.changes`]="{item}">
        <div class="changes-chips">
          <v-chip v-for="cat in categories(item)" :key="cat.key" outlined>
            <v-icon left x-small :color="cat.color">mdi-circle</v-icon>
            {{ cat.label }}<span class="grey--text ml-1">{{ cat.count }}</span>
          </v-chip>
        </div>
      </template>

      <template v-slot:expanded-item="{headers, item}">
        <td :colspan="headers.length" class="expanded-cell" style="word-break: break-word; max-width: 0">
          <div v-for="cat in categories(item)" :key="cat.key" class="mb-6">
            <div class="d-flex align-center mb-2">
              <v-icon x-small :color="cat.color" class="mr-1">mdi-circle</v-icon>
              <span class="font-weight-bold text-uppercase">{{ cat.label }}</span>
            </div>
            <div v-for="entry in cat.items" :key="entry.id" class="d-flex mb-1 ml-4">
              <span class="mr-2 grey--text">–</span>
              <span>{{ entry.description }}</span>
            </div>
          </div>
          <div v-if="item.publishedByLabel" class="caption grey--text mt-3">
            {{ $t('release-notes.published-by') }} {{ item.publishedByLabel }}
          </div>
        </td>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import API from '@/services/api'
import ActionButton from '@/components/Common/ActionButton'
import { mapActions, mapGetters } from "vuex";
import { pick } from 'lodash';

const CATEGORY_DEFS = {
  new: {color: 'amber'},
  improved: {color: 'teal'},
  fixed: {color: 'blue'},
  security: {color: 'pink'}
}

export default {
  name: 'ReleaseNotes',

  components: {
    ActionButton
  },

  data: () => ({
    releaseNotes: [],
    expanded: [],
    loading: false
  }),

  computed: {
    ...mapGetters("currentuser", ["currentUser", "isImitatingUser"]),

    headers() {
      return [
        {text: this.$t('release-notes.col-version'), value: 'version', width: '120px', sortable: false},
        {text: this.$t('release-notes.col-summary'), value: 'summary', sortable: false},
        {text: this.$t('release-notes.col-date'), value: 'releaseDate', width: '130px', sortable: false},
        {text: this.$t('release-notes.col-changes'), value: 'changes', sortable: false},
        {text: '', value: 'data-table-expand'}
      ]
    }
  },

  created() {
    this.load()
  },

  methods: {
    ...mapActions('currentuser', ['setCurrentUser']),

    async load() {
      this.loading = true
      try {
        const res = await API.listReleaseNotes()
        this.releaseNotes = res.data || []
        const current = this.releaseNotes.find(r => r.isCurrent)
        this.expanded = current ? [current] : []
      } finally {
        this.loading = false
      }
    },

    formatDate(d) {
      if (!d) return ''
      return String(d).slice(0, 10)
    },

    categories(item) {
      const buckets = {
        new: {...CATEGORY_DEFS.new, label: this.$t('release-notes.category-new'), items: []},
        improved: {...CATEGORY_DEFS.improved, label: this.$t('release-notes.category-improved'), items: []},
        fixed: {...CATEGORY_DEFS.fixed, label: this.$t('release-notes.category-fixed'), items: []},
        security: {...CATEGORY_DEFS.security, label: this.$t('release-notes.category-security'), items: []}
      }
      const items = Array.isArray(item.items) ? [...item.items] : []
      items
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        .forEach(entry => {
          if (buckets[entry.type]) buckets[entry.type].items.push(entry)
        })
      return Object.entries(buckets)
        .filter(([, b]) => b.items.length > 0)
        .map(([key, b]) => ({key, ...b, count: b.items.length}))
    },

    markAllAsRead() {
      if ( !this.releaseNotes.length ) return
      this.markReleaseNotesAsRead()
    },

    async markReleaseNotesAsRead() {
      try {
        const { data } = await API.markReleaseNotesAsRead()

        await this.setCurrentUser({
          ...this.currentUser,
          ...pick(data, ['lastSeenMinasidorReleaseNoteId'])
        })
      } catch (error) {
        console.log(error);
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.changes-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 12px;
  padding-bottom: 12px;
}
::v-deep .v-data-table__wrapper {
  overflow-x: hidden;
}
::v-deep .v-data-table__wrapper td {
  padding: 12px 12px !important;
}
::v-deep tbody tr.v-data-table__expanded__content {
  box-shadow: none;
}
::v-deep tbody tr.v-data-table__expanded__content td {
  padding: 24px 32px !important;
}
</style>
